import { shallowEqual } from "react-redux";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// Others
import { isEmpty } from "lodash";

// Components

// Store
import { useRedirectOnProjectError } from "utils/hooks";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { toggleLoader } from "store/slice/UI";
import { getInsightById, getProjectInsight } from "store/slice/Project/insights/insightsSlice";
import { fetchProjectBrief, getBriefById } from "store/slice/Project/briefs/briefsSlice";
import { fetchPersonSet, getPersonSetByPath } from "store/slice/Project/peopleSet/peopleSetSlice";

// Utils
import { convertBriefData } from "./Brief.functions";

// Data
import { urlPaths } from "enums/urlPaths";
import {
    getInitialFetchResponse,
    getInsightRelatedSltPath,
    TInitialFetch,
    TInitialFetchError,
} from "pages/ProjectDashboard/adapters/formattedData";
import { TBriefData } from "store/slice/Project/projectData/ProjectSlice.types";

const useBriefInitialFetch = (setBriefData: React.Dispatch<React.SetStateAction<TBriefData>>) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();

    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");
    const briefId = searchParams.get("briefId");

    // Data
    const { data: briefData } = useAppSelector(getBriefById(Number(briefId)), shallowEqual);
    const { data: insightData } = useAppSelector(getInsightById(Number(insightId)), shallowEqual);

    // SLT Data
    const relatedSltPath = getInsightRelatedSltPath(insightData)?.ref?.path;
    const sltData = useAppSelector(getPersonSetByPath(relatedSltPath), shallowEqual);

    // Initial fetch
    useEffect(() => {
        const initialFetch = async () => {
            let result = {} as TInitialFetch | TInitialFetchError;
            const promises = [];

            dispatch(toggleLoader(true));

            if (isEmpty(briefData) && projectId && briefId) {
                promises.push(
                    dispatch(
                        fetchProjectBrief({
                            projectId: Number(projectId),
                            path: `/project_${projectId}/brief_list/brief_${briefId}`,
                        })
                    )
                );
            }

            if (isEmpty(insightData) && projectId && insightId) {
                promises.push(
                    dispatch(
                        getProjectInsight({
                            projectId,
                            path: `/project_${projectId}/insight_list/insight_${insightId}`,
                        })
                    )
                );
            }

            if (promises.length && projectId) {
                const response = await Promise.allSettled(promises);
                result = getInitialFetchResponse(response);

                if ("error" in result) {
                    console.error(result.reason);
                    dispatch(toggleLoader(false));
                    navigate(urlPaths.NotFound);
                    return;
                }
            }

            const newInsight = (result as TInitialFetch)?.insight || insightData;
            const newBrief = (result as TInitialFetch)?.brief || briefData;

            const sltPath = relatedSltPath || getInsightRelatedSltPath(newInsight)?.ref?.path;
            const convertedBrief = convertBriefData(newBrief);

            if (convertedBrief) {
                setBriefData(convertedBrief);
            }

            if (!sltData && sltPath && projectId) {
                await dispatch(fetchPersonSet({ projectId, path: sltPath }));
            }

            dispatch(toggleLoader(false));
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};

export default useBriefInitialFetch;
